import { Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import jsPDF from 'jspdf';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ClientCatalogItem, ClientCatalogService } from 'src/app/services/client-catalog.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { DeliveryOutputDto, DeliveryService } from 'src/generated_client';
import { HttpClient } from '@angular/common/http';
import { SelectPricingRuleDialogComponent, PricingRule } from './select-pricing-rule-dialog/select-pricing-rule-dialog.component';
import { MatTableExporterDirective } from 'mat-table-exporter';

interface SessionForLiquidation {
  cdSession: number;
  dtStartTime: string;
  dtEndTime: string | null;
  dsClient: string | null;
  workShiftPricingRuleId: number | null;
  category: number;
  categoryName: string;
  unitPrice: number;
  hours: number;
  totalCost: number;
}

interface LiquidationTotals {
  totalUnitPrice: number;
  totalHours: number;
  totalCost: number;
  totalSessions: number;
}

interface LiquidationCalculationResponse {
  cdDelivery: number;
  dsObservation: string | null;
  listCdWorkShiftSession: number[];
  sessions: SessionForLiquidation[];
  totals: LiquidationTotals;
}

@Component({
  selector: 'app-liquidaciones-work-shift-sessions',
  templateUrl: './liquidaciones-work-shift-sessions.component.html',
  styleUrls: ['./liquidaciones-work-shift-sessions.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class LiquidacionesWorkShiftSessionsComponent implements OnInit, OnDestroy {

  displayedColumns: string[] = ['select', 'cdSession', 'dtStartTime', 'dtEndTime', 'dsClient', 'categoryName', 'unitPrice', 'hours', 'totalCost', 'acciones'];
  selection = new SelectionModel<SessionForLiquidation>(true, []);
  @ViewChild(MatTable) table!: MatTable<SessionForLiquidation>;
  @ViewChild(MatTableExporterDirective) exporter?: MatTableExporterDirective;
  dataSource = new MatTableDataSource<SessionForLiquidation>([]);
  allDeliveries: DeliveryOutputDto[] = [];
  allClients: ClientCatalogItem[] = [];
  dateFrom: any;
  dateTo: any;
  totalHours: number = 0;
  totalCost: number = 0;
  totalSessions: number = 0;
  observaciones: string = '';
  sessionIdsList: number[] = [];
  password: string = '';
  currentUser: any;
  basePath: string = '';
  isSubmitting = false;
  private destroy$ = new Subject<void>();

  pricingRules: PricingRule[] = [];

  private categoryDescriptions: { [key: number]: string } = {
    0: 'Hs Fija',
    1: 'Hs Eventual',
    2: 'Hs Feriado 50%',
    3: 'Hs Event. Feriado 50%',
    4: 'Hs Feriado 100%',
    5: 'Hs Event. Feriado 100%',
    6: 'Envíos fuera del servicio'
  };

  cadetesForm: FormGroup = this.fb.group({
    cadete: ['', Validators.required],
    puntoFijo: [''],
  });

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach(row => this.selection.select(row));
    this.recalculateTotals();
  }

  toggle(row: SessionForLiquidation) {
    this.selection.toggle(row);
    this.recalculateTotals();
  }

  private recalculateTotals() {
    this.totalHours = 0;
    this.totalCost = 0;

    const sessionsToCalculate = this.selection.selected.length > 0
      ? this.selection.selected
      : this.dataSource.data;

    this.totalSessions = sessionsToCalculate.length;

    sessionsToCalculate.forEach((session: SessionForLiquidation) => {
      this.totalHours += session.hours ?? 0;
      this.totalCost += session.totalCost ?? 0;
    });

    this.totalHours = Number(this.totalHours.toFixed(2));
    this.totalCost = Number(this.totalCost.toFixed(2));
  }

  constructor(
    public deliveryService: DeliveryService,
    private authService: AuthService,
    private fb: FormBuilder,
    private snackBar: SnackBarService,
    private http: HttpClient,
    private dialog: MatDialog,
    private clientCatalog: ClientCatalogService,
  ) {
    this.basePath = (this.deliveryService as any).basePath || '';
  }

  private toArray<T>(response: any): T[] {
    if (Array.isArray(response)) return response;
    const candidates = [response?.data, response?.items, response?.result, response?.values];
    return candidates.find(Array.isArray) ?? [];
  }

  ngOnInit() {
    this.deliveryService.apiDeliveryGetAllGet().subscribe(response => {
      this.allDeliveries = this.toArray<DeliveryOutputDto>(response);
    }, error => {
      console.error('Error al cargar cadetes', error);
    });

    this.loadClients();
    this.loadPricingRules();

    this.cadetesForm.get('cadete')?.valueChanges.subscribe(() => {
      this.clearData();
    });
    this.cadetesForm.get('puntoFijo')?.valueChanges.subscribe(() => {
      this.clearData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadClients(): void {
    this.clientCatalog.getAll(['Punto Fijo']).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: clients => this.allClients = clients,
      error: err => {
        console.error('Error al cargar puntos fijos:', err);
        this.allClients = [];
        this.snackBar.openSnackBar('No se pudieron cargar los clientes de Punto Fijo.', 'Aceptar');
      }
    });
  }

  private loadPricingRules() {
    const url = `${this.basePath}/api/WorkShiftPricingRules/GetAll`;
    this.http.get<any>(url).subscribe({
      next: (response) => {
        const rules = this.toArray<any>(response);
        this.pricingRules = rules.map((r: any) => ({
          id: r.id,
          category: r.category,
          categoryName: this.categoryDescriptions[r.category] || String(r.category),
          type: r.type,
          value: r.value,
          isActive: r.isActive
        }));
      },
      error: (err) => {
        console.error('Error al cargar reglas de precios:', err);
        this.pricingRules = [];
      }
    });
  }

  private clearData() {
    this.dataSource.data = [];
    this.selection.clear();
    this.sessionIdsList = [];
    this.totalHours = 0;
    this.totalCost = 0;
    this.totalSessions = 0;
    this.observaciones = '';
  }

  inpuDateFrom(event: any) {
    this.dateFrom = event.value;
    this.clearData();
  }

  inpuDateTo(event: any) {
    this.dateTo = event.value;
    this.clearData();
  }

  search(form: any) {
    this.clearData();

    let dateFrom = this.dateFrom ? moment(this.dateFrom).startOf('day').toDate() : undefined;
    let dateTo = this.dateTo ? moment(this.dateTo).endOf('day').toDate() : undefined;

    const getIdsUrl = `${this.basePath}/api/WorkShiftSession/GetLiquidationIdsByDeliveryAndDate`;
    let params: any = { cdDelivery: form.cadete };
    if (dateFrom) params.dtFrom = dateFrom.toISOString();
    if (dateTo) params.dtTo = dateTo.toISOString();
    if (form.puntoFijo) {
      // Filtro primario por id (preciso e insensible a mayúsculas) + nombre como
      // fallback para sesiones legacy que todavía no tienen cd_client cargado.
      params.cdClient = form.puntoFijo;
      const pf = this.allClients.find(c => c.cdClient === form.puntoFijo);
      const pfName = pf ? (pf.dsNombreFantasia || pf.dsRazonSocial) : null;
      if (pfName) { params.dsClient = pfName; }
    }

    this.http.get<number[] | any>(getIdsUrl, { params }).subscribe((response: number[] | any) => {
      const sessionIds = this.toArray<number>(response);

      if (sessionIds && sessionIds.length > 0) {
        this.fetchLiquidation(form.cadete, sessionIds);
      } else {
        this.snackBar.openSnackBar('No se encontraron turnos para los criterios especificados', 'Aceptar');
      }
    }, error => {
      this.snackBar.openSnackBar('Error al obtener los turnos', 'Aceptar');
    });
  }

  private fetchLiquidation(cdDelivery: number, sessionIds: number[]) {
    const calculateUrl = `${this.basePath}/api/WorkShiftSession/CalculateLiquidation`;
    let calcParams: any = {
      cdDelivery: cdDelivery.toString()
    };
    sessionIds.forEach((id, index) => {
      calcParams[`listCdWorkShiftSession[${index}]`] = id.toString();
    });

    this.http.get<LiquidationCalculationResponse>(calculateUrl, { params: calcParams }).subscribe((data: LiquidationCalculationResponse) => {
      this.dataSource.data = data.sessions || [];

      if (data.totals) {
        this.totalHours = Number((data.totals.totalHours ?? 0).toFixed(2));
        this.totalCost = Number((data.totals.totalCost ?? 0).toFixed(2));
        this.totalSessions = data.totals.totalSessions ?? 0;
      } else {
        this.recalculateTotals();
      }

      this.sessionIdsList = this.dataSource.data.map(s => s.cdSession);
    }, error => {
      this.snackBar.openSnackBar('Error al calcular la liquidación', 'Aceptar');
    });
  }

  openEditRuleDialog(session: SessionForLiquidation, event: Event) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(SelectPricingRuleDialogComponent, {
      width: '480px',
      data: {
        rules: this.pricingRules,
        currentRuleId: session.workShiftPricingRuleId ?? null
      }
    });

    dialogRef.afterClosed().subscribe(selectedRuleId => {
      if (selectedRuleId !== null && selectedRuleId !== undefined) {
        this.savePricingRule(session, selectedRuleId);
      }
    });
  }

  private savePricingRule(session: SessionForLiquidation, ruleId: number) {
    const updateUrl = `${this.basePath}/api/WorkShiftSession/Update`;
    const payload = {
      id: session.cdSession,
      workShiftPricingRuleId: ruleId
    };

    this.http.put(updateUrl, payload).subscribe(() => {
      const rule = this.pricingRules.find(r => r.id === ruleId);
      if (rule) {
        session.workShiftPricingRuleId = ruleId;
        session.category = rule.category;
        session.categoryName = rule.categoryName;
        session.unitPrice = rule.value;
        session.totalCost = Number((session.hours * rule.value).toFixed(2));
      }

      this.recalculateTotals();
      this.snackBar.openSnackBar('Regla asignada correctamente.', 'Aceptar');
    }, error => {
      console.error('Error al guardar la regla:', error);
      this.snackBar.openSnackBar('Error al guardar la regla', 'Aceptar');
    });
  }

  confirmLiquidation() {
    if (this.isSubmitting) {
      return;
    }

    this.currentUser = this.authService.currentUser();
    this.sessionIdsList = this.selection.selected.map(s => s.cdSession);

    if (this.sessionIdsList.length === 0) {
      this.snackBar.openSnackBar('No hay turnos seleccionados. Por favor, selecciona al menos uno para liquidar.', 'Aceptar');
      return;
    }

    if (!this.password) {
      this.snackBar.openSnackBar('Debe ingresar la contraseña de administrador.', 'Aceptar');
      return;
    }

    this.recalculateTotals();
    this.isSubmitting = true;

    const liquidationPayload = {
      cdDelivery: this.cadetesForm.get('cadete')!.value,
      listCdWorkShiftSession: this.sessionIdsList,
      dsObservation: this.observaciones,
      dsUsername: this.currentUser?.dsUsername,
      dsPassword: this.password,
    };

    const liquidateUrl = `${this.basePath}/api/WorkShiftSession/PostLiquidation`;
    this.http.post(liquidateUrl, liquidationPayload).subscribe(() => {
      this.snackBar.openSnackBar('Liquidación emitida con éxito.', 'Aceptar');
      this.password = '';
      this.clearData();
      this.isSubmitting = false;
    }, error => {
      this.snackBar.openSnackBar(this.extractError(error, 'Error al procesar la liquidación'), 'Aceptar');
      this.isSubmitting = false;
    });
  }

  private extractError(error: any, fallback: string): string {
    const raw = error?.error;
    if (typeof raw === 'string') {
      return raw;
    }
    if (raw?.error) {
      return raw.error;
    }
    if (raw?.title) {
      return raw.title;
    }
    return fallback;
  }

  private getRowsToExport(): SessionForLiquidation[] {
    if (this.selection.selected.length > 0) {
      return this.selection.selected;
    }
    return this.dataSource.data;
  }

  exportExcel(): void {
    const rows = this.getRowsToExport();

    if (!rows.length) {
      this.snackBar.openSnackBar('No hay datos para exportar.', 'Aceptar');
      return;
    }

    if (!this.exporter) {
      return;
    }

    // matTableExporter exporta el dataSource completo; se intercambia
    // temporalmente por las filas a exportar (selección si hay, si no todas).
    const fullData = this.dataSource.data;
    if (rows !== fullData) {
      this.dataSource.data = rows;
    }

    try {
      this.exporter.exportTable('xlsx', { fileName: 'liquidacion-punto-fijo.xlsx' });
    } finally {
      if (rows !== fullData) {
        this.dataSource.data = fullData;
      }
    }
  }

  exportPDF(): void {
    const rows = this.getRowsToExport();

    if (!rows.length) {
      this.snackBar.openSnackBar('No hay datos para exportar.', 'Aceptar');
      return;
    }

    const doc = new jsPDF();
    const cadeteId = this.cadetesForm.get('cadete')?.value;
    const cadete = this.allDeliveries.find(d => d.cdDelivery === cadeteId);
    const puntoFijoId = this.cadetesForm.get('puntoFijo')?.value;
    const pf = this.allClients.find(c => c.cdClient === puntoFijoId);
    const puntoFijo = pf ? (pf.dsNombreFantasia || pf.dsRazonSocial) : null;

    let y = 10;
    doc.setFontSize(14);
    doc.text('Liquidación Punto Fijo', 10, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Cadete: ${cadete?.dsName ?? '-'}`, 10, y);
    y += 6;
    doc.text(`Punto fijo: ${puntoFijo || 'Todos'}`, 10, y);
    y += 6;
    doc.text(`Desde: ${this.dateFrom ? moment(this.dateFrom).format('DD/MM/YYYY') : '-'}   Hasta: ${this.dateTo ? moment(this.dateTo).format('DD/MM/YYYY') : '-'}`, 10, y);
    y += 6;
    doc.text(`Fecha de emisión: ${moment().format('DD/MM/YYYY HH:mm')}`, 10, y);
    y += 8;

    doc.setFontSize(8);
    const cols = [8, 24, 62, 100, 135, 165, 182, 196];
    doc.text('TURNO', cols[0], y);
    doc.text('INICIO', cols[1], y);
    doc.text('FIN', cols[2], y);
    doc.text('CLIENTE', cols[3], y);
    doc.text('CATEGORÍA', cols[4], y);
    doc.text('UNITARIO', cols[5], y);
    doc.text('HORAS', cols[6], y);
    doc.text('TOTAL', cols[7], y);
    y += 5;

    rows.forEach(row => {
      if (y > 285) {
        doc.addPage();
        y = 15;
      }
      doc.text(String(row.cdSession), cols[0], y);
      doc.text(moment(row.dtStartTime).format('DD/MM/YY HH:mm'), cols[1], y);
      doc.text(row.dtEndTime ? moment(row.dtEndTime).format('DD/MM/YY HH:mm') : '-', cols[2], y);
      doc.text((row.dsClient ?? '-').substring(0, 22), cols[3], y);
      doc.text((row.categoryName ?? '-').substring(0, 20), cols[4], y);
      doc.text(String(row.unitPrice ?? '-'), cols[5], y);
      doc.text(String(row.hours ?? '-'), cols[6], y);
      doc.text(String(row.totalCost ?? '-'), cols[7], y);
      y += 5;
    });

    y += 5;
    doc.setFontSize(10);
    doc.text(`Total horas: ${this.totalHours}   Costo total: ${this.totalCost}   Turnos: ${this.totalSessions}`, 10, y);
    if (this.observaciones) {
      y += 6;
      doc.text(`Observaciones: ${this.observaciones}`, 10, y);
    }

    doc.save('liquidacion-punto-fijo.pdf');
  }
}
